import { useState, useEffect } from "react";
import { GuiButton } from "../../components/global-ui/gui-button";
import GuiCustomDatepickerDropdown from "../../components/global-ui/gui-custom-datepicker-dropdown";
import { showToast } from "../../components/toast";
import { useUpdateState } from "../../utilities/utilities";
import account from "../../actions/account-actions";
import {
  DEFAULT_STUDENT_LABEL,
  DEFAULT_FACULTY_LABEL,
} from "../../utilities/constants";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
}

function pick(source, camelKey, pascalKey, fallback) {
  return source?.[camelKey] ?? source?.[pascalKey] ?? fallback;
}

function formatImportDate(value) {
  if (!value) return "Not imported";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Not imported";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AccountImports({ summaryData }) {
  const studentLastImported = pick(
    summaryData,
    "studentLastImported",
    "StudentLastImported",
    null,
  );
  const facultyLastImported = pick(
    summaryData,
    "facultyLastImported",
    "FacultyLastImported",
    null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState({
    student: DEFAULT_STUDENT_LABEL,
    faculty: DEFAULT_FACULTY_LABEL,
  });
  const today = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate(),
  );
  const [saved, updateSaved] = useUpdateState({
    importStopDate: null,
    importRestartDate: null,
    autoCallStopDate: null,
    autoCallRestartDate: null,
  });
  const [form, updateForm] = useUpdateState({
    importStopDate: null,
    importRestartDate: null,
    autoCallStopDate: null,
    autoCallRestartDate: null,
  });
  const [errors, updateErrors] = useUpdateState({
    importDate: null,
    autoCallDate: null,
  });

  useEffect(() => {
    account.getImportSettings().then((result) => {
      if (result?.success) {
        const vals = {
          importStopDate: result?.importStopDate ?? null,
          importRestartDate: result?.importStartDate ?? null,
          autoCallStopDate: result?.autoCallStopDate ?? null,
          autoCallRestartDate: result?.autoCallStartDate ?? null,
        };
        updateSaved(vals);
        updateForm(vals);
      }
      setLoading(false);
    });

    account.getPermissions().then((result) => {
      if (result?.success) {
        setLabels({
          student: result?.studentLabel || DEFAULT_STUDENT_LABEL,
          faculty: result?.facultyLabel || DEFAULT_FACULTY_LABEL,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    // AC9: Validate Re-Start Date must be >= Stop Date for Import
    if (
      form.importStopDate &&
      form.importRestartDate &&
      new Date(form.importRestartDate) < new Date(form.importStopDate)
    ) {
      updateErrors({
        importDate:
          "Re-Start Date must be the same as or later than the Stop Date.",
      });
      return;
    }
    // AC9: Validate Re-Start Date must be >= Stop Date for AutoCall
    if (
      form.autoCallStopDate &&
      form.autoCallRestartDate &&
      new Date(form.autoCallRestartDate) < new Date(form.autoCallStopDate)
    ) {
      updateErrors({
        autoCallDate:
          "Re-Start Date must be the same as or later than the Stop Date.",
      });
      return;
    }
    updateErrors({ importDate: null, autoCallDate: null });

    const result = await account.saveImportSettings({
      importStopDate: form.importStopDate,
      importStartDate: form.importRestartDate,
      autoCallStopDate: form.autoCallStopDate,
      autoCallStartDate: form.autoCallRestartDate,
    });
    if (result?.success) {
      const vals = {
        importStopDate: result.importStopDate ?? null,
        importRestartDate: result.importStartDate ?? null,
        autoCallStopDate: result.autoCallStopDate ?? null,
        autoCallRestartDate: result.autoCallStartDate ?? null,
      };
      updateSaved(vals);
      updateForm(vals);
      showToast("success", "Changes saved successfully.");
      setIsEditing(false);
    } else {
      showToast("error", "Changes could not be saved. Please try again.");
    }
  }

  function handleCancel() {
    updateForm({ ...saved });
    updateErrors({ importDate: null, autoCallDate: null });
    setIsEditing(false);
  }

  return (
    <div className="acct-imports">
      {/* ── Header ── */}
      <div className="acct-imports__header">
        <div className="acct-imports__title-row">
          <span className="acct-imports__title">
            Suspend Imports &amp; Auto Calls
          </span>
          {!isEditing && !loading && (
            <GuiButton
              className="btn-outline acct-imports__edit-btn"
              onClick={() => setIsEditing(true)}
            >
              <i className="fas fa-pen" /> Edit
            </GuiButton>
          )}
        </div>
        <div className="acct-imports__info-banner">
          <i className="fas fa-info-circle" />
          Auto-Calls include Attendance and Lunch Balance calls
        </div>
      </div>

      {/* ── Table ── */}
      <div
        className={`acct-imports__table${isEditing ? " acct-imports__table--editing" : ""}`}
      >
        {/* Header row */}
        <div className="acct-imports__table-row acct-imports__table-row--head">
          <div className="acct-imports__col-label" />
          <div className="acct-imports__col-stop-date acct-imports__col--header">
            Stop Date
          </div>
          <div className="acct-imports__col-restart acct-imports__col--header">
            Re-Start
          </div>
        </div>

        {/* Import row */}
        <div className="acct-imports__table-row">
          <div className="acct-imports__col-label">Import</div>
          {isEditing ? (
            <>
              <div className="acct-imports__col-stop-date acct-imports__col--input">
                <GuiCustomDatepickerDropdown
                  dateSelected={form.importStopDate}
                  onDateChange={(val) => updateForm({ importStopDate: val })}
                  minDate={today}
                />
              </div>
              <div className="acct-imports__col-restart acct-imports__col--input">
                <GuiCustomDatepickerDropdown
                  dateSelected={form.importRestartDate}
                  onDateChange={(val) => updateForm({ importRestartDate: val })}
                  minDate={today}
                />
              </div>
            </>
          ) : (
            <>
              <div className="acct-imports__col-stop-date">
                {formatDate(saved.importStopDate)}
              </div>
              <div className="acct-imports__col-restart">
                {formatDate(saved.importRestartDate)}
              </div>
            </>
          )}
        </div>
        {isEditing && errors.importDate && (
          <div className="acct-imports__validation-error">
            {errors.importDate}
          </div>
        )}

        {/* AutoCall row */}
        <div className="acct-imports__table-row">
          <div className="acct-imports__col-label">AutoCall</div>
          {isEditing ? (
            <>
              <div className="acct-imports__col-stop-date acct-imports__col--input">
                <GuiCustomDatepickerDropdown
                  dateSelected={form.autoCallStopDate}
                  onDateChange={(val) => updateForm({ autoCallStopDate: val })}
                  minDate={today}
                />
              </div>
              <div className="acct-imports__col-restart acct-imports__col--input">
                <GuiCustomDatepickerDropdown
                  dateSelected={form.autoCallRestartDate}
                  onDateChange={(val) =>
                    updateForm({ autoCallRestartDate: val })
                  }
                  minDate={today}
                />
              </div>
            </>
          ) : (
            <>
              <div className="acct-imports__col-stop-date">
                {formatDate(saved.autoCallStopDate)}
              </div>
              <div className="acct-imports__col-restart">
                {formatDate(saved.autoCallRestartDate)}
              </div>
            </>
          )}
        </div>
        {isEditing && errors.autoCallDate && (
          <div className="acct-imports__validation-error">
            {errors.autoCallDate}
          </div>
        )}
      </div>

      {/* ── Actions (edit mode only) ── */}
      {isEditing && (
        <div className="acct-imports__actions">
          <GuiButton
            className="btn-outline acct-imports__cancel-btn"
            onClick={handleCancel}
          >
            Cancel
          </GuiButton>
          <GuiButton
            className="btn-primary acct-imports__save-btn"
            onClick={handleSave}
          >
            <i className="fas fa-save" /> Save
          </GuiButton>
        </div>
      )}

      {/* ── Last import dates ── */}
      <div className="plan-details__import-dates acct-imports__import-dates">
        <div className="plan-details__import-item">
          <span className="plan-details__import-label">
            {labels.student} Data Last Imported
          </span>
          <span className="plan-details__import-value">
            {formatImportDate(studentLastImported)}
          </span>
        </div>
        <div className="plan-details__import-item">
          <span className="plan-details__import-label">
            {labels.faculty} Data Last Imported
          </span>
          <span className="plan-details__import-value">
            {formatImportDate(facultyLastImported)}
          </span>
        </div>
      </div>
    </div>
  );
}
